import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Center,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Textarea,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { ReadOnlyTable, type TableColumn } from '@resistapp/client/components/tables/read-only-table';
import { LabWorkbookStatus } from '@resistapp/common/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useToastWithoutDebounce } from '../../../hooks/use-resistomap-toast';
import { markLabWorkbookReady, patchLabWorkbook } from '../api/update-lab-workbook';
import { useLabSamplePlates, type LabTubePickRow } from '../api/use-lab-sample-plates';
import { LabPageHeader } from '../components/lab-page-header';
import { useLabLayoutContext } from '../lab-layout';
import { isStatusAtLeast } from '../status-utils';

function normalizeText(value: string | null | undefined): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

type StorageField = 'sampleStorageFindLocation' | 'sampleStorageLeaveLocation';
type WorkbookDetailsPayload = Partial<Record<StorageField, string | null>> & { notes?: string | null };

export function SamplePlatesPage() {
  const { workbookQuery, workbookId, viewerAccess, readOnly } = useLabLayoutContext();
  const samplePrepReadOnly = readOnly.samplePrep;
  const { data, error, isLoading, isError } = workbookQuery;
  const workbook = data?.workbook;
  const sampleSheet = data?.sampleSheet;
  const labPartnerNotesReadOnly = isStatusAtLeast(workbook?.status, LabWorkbookStatus.COMPLETED);
  const sampleStorageReadOnly = labPartnerNotesReadOnly;
  const samplePlatesQuery = useLabSamplePlates(data);
  const sampleData = samplePlatesQuery.data;
  const queryClient = useQueryClient();
  const showToast = useToastWithoutDebounce();
  const chipsWithData = useMemo(() => sampleData?.chips.map(chip => chip.chipNumber) ?? [], [sampleData]);
  const chipTabNumbers = useMemo(() => {
    const configuredChipCount = workbook?.numChips ?? 0;
    const maxFromData = chipsWithData.length > 0 ? Math.max(...chipsWithData) : 0;
    const target = Math.max(configuredChipCount, maxFromData);
    if (target <= 0) {
      return chipsWithData;
    }
    return Array.from({ length: target }, (_value, index) => index + 1);
  }, [chipsWithData, workbook?.numChips]);

  const [labPartnerNotes, setLabPartnerNotes] = useState('');
  const [sampleStorageFind, setSampleStorageFind] = useState('');
  const [sampleStorageLeave, setSampleStorageLeave] = useState('');
  const labPartnerNotesServerRef = useRef('');
  const sampleStorageFindServerRef = useRef('');
  const sampleStorageLeaveServerRef = useRef('');
  const markReadyDisclosure = useDisclosure();

  const [selectedChip, setSelectedChip] = useState<number | null>(null);

  useEffect(() => {
    const nextNotes = workbook?.notes ?? '';
    const lastServerNotes = labPartnerNotesServerRef.current;
    if (nextNotes !== lastServerNotes && labPartnerNotes === lastServerNotes) {
      setLabPartnerNotes(nextNotes);
    }
    if (nextNotes !== lastServerNotes) {
      labPartnerNotesServerRef.current = nextNotes;
    }
  }, [labPartnerNotes, workbook?.notes]);

  useEffect(() => {
    const nextSampleFind = workbook?.sampleStorageFindLocation ?? '';
    const lastServerFind = sampleStorageFindServerRef.current;
    if (nextSampleFind !== lastServerFind && sampleStorageFind === lastServerFind) {
      setSampleStorageFind(nextSampleFind);
    }
    if (nextSampleFind !== lastServerFind) {
      sampleStorageFindServerRef.current = nextSampleFind;
    }
  }, [sampleStorageFind, workbook?.sampleStorageFindLocation]);

  useEffect(() => {
    const nextSampleLeave = workbook?.sampleStorageLeaveLocation ?? '';
    const lastServerLeave = sampleStorageLeaveServerRef.current;
    if (nextSampleLeave !== lastServerLeave && sampleStorageLeave === lastServerLeave) {
      setSampleStorageLeave(nextSampleLeave);
    }
    if (nextSampleLeave !== lastServerLeave) {
      sampleStorageLeaveServerRef.current = nextSampleLeave;
    }
  }, [sampleStorageLeave, workbook?.sampleStorageLeaveLocation]);

  useEffect(() => {
    if (chipTabNumbers.length === 0) {
      if (selectedChip !== null) {
        setSelectedChip(null);
      }
      return;
    }
    if (selectedChip == null || !chipTabNumbers.includes(selectedChip)) {
      setSelectedChip(chipTabNumbers[0] ?? null);
    }
  }, [chipTabNumbers, selectedChip]);

  const numberFormatter = useMemo(
    () =>
      new Intl.NumberFormat('en-US', {
        maximumFractionDigits: 2,
      }),
    [],
  );

  const workbookDetailsMutation = useMutation({
    mutationFn: async (payload: WorkbookDetailsPayload) => {
      if (!workbookId) {
        throw new Error('Missing workbook identifier');
      }
      return await patchLabWorkbook(workbookId, payload);
    },
    onSuccess: summary => {
      queryClient.setQueryData(['labWorkbook', workbookId], summary);
    },
    onError: mutationError => {
      const message = mutationError instanceof Error ? mutationError.message : 'Failed to update sample prep details';
      showToast({ status: 'error', title: message });
    },
  });

  const handleStorageBlur = useCallback(
    (field: StorageField, value: string) => {
      if (sampleStorageReadOnly || !workbook) {
        return;
      }
      const normalized = normalizeText(value);
      const currentRaw =
        field === 'sampleStorageFindLocation'
          ? workbook.sampleStorageFindLocation
          : workbook.sampleStorageLeaveLocation;
      const currentNormalized = currentRaw == null ? null : normalizeText(currentRaw);
      if (normalized === currentNormalized) {
        return;
      }
      workbookDetailsMutation.mutate({ [field]: normalized });
    },
    [sampleStorageReadOnly, workbookDetailsMutation, workbook],
  );

  const handleNotesBlur = useCallback(
    (value: string) => {
      if (labPartnerNotesReadOnly || !workbook) {
        return;
      }
      const normalized = normalizeText(value);
      const currentRaw = workbook.notes;
      const currentNormalized = currentRaw == null ? null : normalizeText(currentRaw);
      if (normalized === currentNormalized) {
        return;
      }
      workbookDetailsMutation.mutate({ notes: normalized });
    },
    [labPartnerNotesReadOnly, workbook, workbookDetailsMutation],
  );

  const markReadyMutation = useMutation({
    mutationFn: async () => {
      if (!workbookId) {
        throw new Error('Missing workbook identifier');
      }
      const payload = {
        notes: normalizeText(labPartnerNotes),
      };
      return await markLabWorkbookReady(workbookId, payload);
    },
    onSuccess: summary => {
      queryClient.setQueryData(['labWorkbook', workbookId], summary);
      markReadyDisclosure.onClose();
      showToast({ status: 'success', title: 'Workbook marked as samples prepped' });
    },
    onError: mutationError => {
      const message =
        mutationError instanceof Error ? mutationError.message : 'Failed to mark workbook as samples prepped';
      showToast({ status: 'error', title: message });
    },
  });

  const formatPickListNumeric = useCallback(
    (value: unknown) => {
      if (value == null) {
        return '—';
      }
      if (typeof value === 'number' && Number.isFinite(value)) {
        return numberFormatter.format(value);
      }
      const parsed = Number(value);
      if (Number.isFinite(parsed)) {
        return numberFormatter.format(parsed);
      }
      return String(value);
    },
    [numberFormatter],
  );

  const pickListColumns = useMemo<Array<TableColumn<LabTubePickRow>>>(
    () => [
      {
        key: 'chipNumber',
        header: 'Chip',
        field: 'chipNumber',
        width: 100,
        align: 'right',
        format: value => (value == null ? '—' : String(value)),
      },
      {
        key: 'sampleId',
        header: 'Sample ID',
        field: 'sampleId',
        width: 140,
        align: 'right',
        format: value => (value == null ? '—' : String(value)),
      },
      {
        key: 'sampleLabel',
        header: 'Sample label',
        field: 'sampleLabel',
        width: 160,
        format: value => (value == null || value === '' ? '—' : String(value)),
      },
      {
        key: 'sampleDescription',
        header: 'Description',
        field: 'sampleDescription',
        width: 260,
        format: value => (value == null || value === '' ? '—' : String(value)),
      },
      {
        key: 'sampleTypeLabel',
        header: 'Type',
        field: 'sampleTypeLabel',
        width: 180,
        format: value => (value == null || value === '' ? '—' : String(value)),
      },
      {
        key: 'sampleSubtypeLabel',
        header: 'Subtype',
        field: 'sampleSubtypeLabel',
        width: 200,
        format: value => (value == null || value === '' ? '—' : String(value)),
      },
      {
        key: 'primDilutedDnaConcentration',
        header: 'Diluted conc. (ng/µl)',
        field: 'primDilutedDnaConcentration',
        width: 220,
        align: 'right',
        format: value => formatPickListNumeric(value),
      },
      {
        key: 'primDilutedDnaQuality280',
        header: 'Diluted 260/280',
        field: 'primDilutedDnaQuality280',
        width: 180,
        align: 'right',
        format: value => formatPickListNumeric(value),
      },
      {
        key: 'primDilutedDnaQuality230',
        header: 'Diluted 260/230',
        field: 'primDilutedDnaQuality230',
        width: 180,
        align: 'right',
        format: value => formatPickListNumeric(value),
      },
      {
        key: 'primElutedDnaConcentration',
        header: 'Eluted conc. (ng/µl)',
        field: 'primElutedDnaConcentration',
        width: 220,
        align: 'right',
        format: value => formatPickListNumeric(value),
      },
      {
        key: 'filteredVolume',
        header: 'Filtered volume (mL)',
        field: 'filteredVolume',
        width: 220,
        align: 'right',
        format: value => formatPickListNumeric(value),
      },
    ],
    [formatPickListNumeric],
  );

  const tubePickRowsByChip = useMemo(() => {
    const map = new Map<number, LabTubePickRow[]>();
    if (!sampleData) {
      return map;
    }
    sampleData.tubePickList.forEach(row => {
      const bucket = map.get(row.chipNumber);
      if (bucket) {
        bucket.push(row);
      } else {
        map.set(row.chipNumber, [row]);
      }
    });
    return map;
  }, [sampleData]);

  const getPickRowId = useCallback((row: LabTubePickRow) => `${row.sampleId}`, []);

  const isDerivedLoading = samplePlatesQuery.isLoading && !sampleData;

  if (!viewerAccess.canViewSamplePrep) {
    return (
      <Alert status="warning" borderRadius="md">
        <AlertIcon />
        <AlertDescription>
          You do not have access to the sample preparation step for this lab workbook.
        </AlertDescription>
      </Alert>
    );
  }

  if (!workbookId) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        <AlertDescription>Missing workbook identifier in URL.</AlertDescription>
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <Center minH="240px">
        <Spinner size="lg" />
      </Center>
    );
  }

  if (isError) {
    const message = error instanceof Error ? error.message : 'Failed to load workbook';
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        <AlertDescription>{message}</AlertDescription>
      </Alert>
    );
  }

  if (!workbook) {
    return null;
  }

  // Header shows the status badge; no local color mapping needed here.
  const labSheetUrl = sampleSheet ? sampleSheet.labSheetUrl : null;
  const channelUrl = workbook.channelUrl;
  const workbookName = workbook.name ? workbook.name.trim() : '';
  const baseHeaderTitle = 'Prepare Samples';
  const headerTitle = workbookName ? `${workbookName} — ${baseHeaderTitle}` : baseHeaderTitle;
  const sampleStorageLocationsMissing =
    normalizeText(sampleStorageFind) == null || normalizeText(sampleStorageLeave) == null;
  const readyButtonDisabled =
    samplePrepReadOnly || workbook.status !== LabWorkbookStatus.PRIMERS_PREPPED || sampleStorageLocationsMissing;
  const needsPrimersFirst =
    workbook.status === LabWorkbookStatus.DRAFT ||
    workbook.status === LabWorkbookStatus.SAMPLE_SELECTION ||
    workbook.status === LabWorkbookStatus.SUBMITTED ||
    workbook.status === LabWorkbookStatus.MEASURED;

  return (
    <VStack align="stretch" spacing={6}>
      <LabPageHeader
        title={headerTitle}
        subtitle="Order sample tubes based on the sample list and review that tubes have enough content."
        labSheetUrl={labSheetUrl}
        channelUrl={channelUrl}
        control={
          !labSheetUrl && !samplePrepReadOnly ? (
            <Button
              colorScheme="teal"
              onClick={() => {
                markReadyDisclosure.onOpen();
              }}
              isDisabled={readyButtonDisabled}
              title={
                sampleStorageLocationsMissing
                  ? 'Fill "Where to find" and "Where to leave" samples before marking them prepped'
                  : undefined
              }
              isLoading={markReadyMutation.isPending}
              alignSelf="flex-start"
            >
              Mark samples prepped
            </Button>
          ) : undefined
        }
      />

      {needsPrimersFirst ? (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <AlertDescription>Mark primers prepared before continuing with sample prep.</AlertDescription>
        </Alert>
      ) : null}
      {samplePrepReadOnly ? (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <AlertDescription>
            Most sample prep details are read only after marking samples prepped. Partner notes and sample storage
            locations stay editable until the workbook is completed.
          </AlertDescription>
        </Alert>
      ) : null}

      <Box borderWidth="1px" borderRadius="md" padding={4}>
        <Heading size="md" fontSize="lg" mb={3}>
          Sample prep notes
        </Heading>
        <VStack align="stretch" spacing={4}>
          <FormControl>
            <FormLabel>Note for lab partner</FormLabel>
            <Textarea
              value={labPartnerNotes}
              onChange={event => {
                if (labPartnerNotesReadOnly) {
                  return;
                }
                setLabPartnerNotes(event.target.value);
              }}
              onBlur={event => {
                handleNotesBlur(event.target.value);
              }}
              minH="96px"
              isDisabled={labPartnerNotesReadOnly || workbookDetailsMutation.isPending}
              placeholder="Add context for the SmartChip run team"
            />
          </FormControl>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <FormControl>
              <FormLabel>Where to find samples</FormLabel>
              <Textarea
                value={sampleStorageFind}
                onChange={event => {
                  if (sampleStorageReadOnly) {
                    return;
                  }
                  setSampleStorageFind(event.target.value);
                }}
                onBlur={event => {
                  handleStorageBlur('sampleStorageFindLocation', event.target.value);
                }}
                minH="96px"
                isDisabled={sampleStorageReadOnly || workbookDetailsMutation.isPending}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Where to leave samples</FormLabel>
              <Textarea
                value={sampleStorageLeave}
                onChange={event => {
                  if (sampleStorageReadOnly) {
                    return;
                  }
                  setSampleStorageLeave(event.target.value);
                }}
                onBlur={event => {
                  handleStorageBlur('sampleStorageLeaveLocation', event.target.value);
                }}
                minH="96px"
                isDisabled={sampleStorageReadOnly || workbookDetailsMutation.isPending}
              />
            </FormControl>
          </SimpleGrid>
        </VStack>
        <Flex justify="flex-end" mt={4} minH="24px" align="center">
          {workbookDetailsMutation.isPending ? (
            <HStack spacing={2}>
              <Spinner size="xs" />
              <Text color="gray.600" fontSize="sm" fontWeight="medium">
                Saving...
              </Text>
            </HStack>
          ) : (
            <Text color="gray.500" fontSize="sm">
              All changes saved
            </Text>
          )}
        </Flex>
      </Box>

      {samplePlatesQuery.isError && (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <AlertDescription>
            {samplePlatesQuery.error?.message ?? 'Failed to load derived sample plate data'}
          </AlertDescription>
        </Alert>
      )}

      {isDerivedLoading && (
        <Center minH="200px">
          <Spinner size="lg" />
        </Center>
      )}

      {!isDerivedLoading && sampleData && (
        <>
          <HStack spacing={3} flexWrap="wrap">
            <Badge colorScheme="purple">{`Chips: ${Math.max(sampleData.chipCount, chipTabNumbers.length)}`}</Badge>
            <Badge colorScheme="blue">
              {`Samples per chip: ${sampleData.samplesPerChip != null ? sampleData.samplesPerChip : 'n/a'}`}
            </Badge>
            <Badge colorScheme="teal">{`Total samples: ${sampleData.totalSamples}`}</Badge>
          </HStack>
          <Box borderWidth="1px" borderRadius="md" padding={4}>
            <Heading size="md" fontSize="lg" mb={3}>
              Tube pick list
            </Heading>
            {chipTabNumbers.length === 0 ? (
              <Text color="gray.600">Tube pick list data will appear once samples are assigned to chips.</Text>
            ) : (
              <Tabs
                index={Math.max(0, chipTabNumbers.indexOf(selectedChip ?? chipTabNumbers[0]))}
                onChange={tabIndex => {
                  const nextChip = chipTabNumbers[tabIndex];
                  if (Number.isFinite(nextChip)) {
                    if (nextChip !== selectedChip) {
                      setSelectedChip(nextChip);
                    }
                  } else if (selectedChip !== null) {
                    setSelectedChip(null);
                  }
                }}
              >
                <TabList>
                  {chipTabNumbers.map(chipNumber => (
                    <Tab key={chipNumber}>{`Chip ${chipNumber}`}</Tab>
                  ))}
                </TabList>
                <TabPanels mt={4}>
                  {chipTabNumbers.map(chipNumber => {
                    const rows = tubePickRowsByChip.get(chipNumber) ?? [];
                    return (
                      <TabPanel key={chipNumber} px={0}>
                        {rows.length === 0 ? (
                          <Text color="gray.600">No tube pick list data available for this chip.</Text>
                        ) : (
                          <ReadOnlyTable<LabTubePickRow>
                            columns={pickListColumns}
                            rows={rows}
                            getRowId={getPickRowId}
                          />
                        )}
                      </TabPanel>
                    );
                  })}
                </TabPanels>
              </Tabs>
            )}
          </Box>
        </>
      )}

      {!isDerivedLoading && !sampleData && !samplePlatesQuery.isError && (
        <Text color="gray.600">Sample sheet data is not available yet for this workbook.</Text>
      )}

      <Modal isOpen={markReadyDisclosure.isOpen && !samplePrepReadOnly} onClose={markReadyDisclosure.onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Mark workbook as samples prepped</ModalHeader>
          <ModalBody>
            <Text mb={3} color="gray.600">
              Optional note for the SmartChip run team (shown on the SmartChip run page).
            </Text>
            <Textarea
              value={labPartnerNotes}
              onChange={event => {
                if (samplePrepReadOnly) {
                  return;
                }
                setLabPartnerNotes(event.target.value);
              }}
              placeholder="Note for lab partner"
              minH="120px"
              isDisabled={samplePrepReadOnly || markReadyMutation.isPending}
            />
          </ModalBody>
          <ModalFooter gap={3}>
            <Button variant="ghost" onClick={markReadyDisclosure.onClose} isDisabled={markReadyMutation.isPending}>
              Cancel
            </Button>
            <Button
              colorScheme="teal"
              onClick={() => {
                if (readyButtonDisabled) {
                  return;
                }
                markReadyMutation.mutate();
              }}
              isDisabled={readyButtonDisabled || markReadyMutation.isPending}
              isLoading={markReadyMutation.isPending}
            >
              Mark samples prepped
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
}
